import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useHistory, useRouteMatch, generatePath } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Scrollbar } from 'react-scrollbars-custom';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { getBuildingTitle } from '../../../store/buildings/actions';
import { newSelectStyles } from '../../../common/shared/select.styled';
import PrintPdfIcons from '../../../common/export_documents/PrintPdfIconsNew';
import PrintPdfDropdown from '../../../common/export_documents/PrintPdfDropdown';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { getSelectMonthForDropdown } from '../../../common/shared/statelessMethods';
import BllinkUtils from '../../../common/utils/utils';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import Search from '../../../images/search.svg';
import { StyledReport } from '../report.styled';
import { StyledMonthlyExpenses } from './MonthlyExpensesNew.styled';
import ExpensesCard from './ExpensesCard2';
import { CurrencyValue } from "../../../common/currencyValue";
import { StyledPdfWrapper } from '../debtList/PdfWrapper.styled';
import ExpensesPdfRow from './ExpensesPdfRow';
import { PageTitle } from '../../../common/PageTitle';

const StringUtils = BllinkUtils.Strings;

const MonthlyExpenses = ({ address }) => {
    const { buildingID, year, month } = useParams();
    const { path } = useRouteMatch()
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const titleData = useSelector((state) => state.buildings.titleData);
    const ltr = useSelector((state) => state.common.ltrEnabled);
    const pdfContainer = useRef();
    const client = useAccessToken();
    const history = useHistory();
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);

    useBodyClass('report');

    const [monthData, setMonthData] = useState({
        monthsTexts: [],
        selectedIndex: 0
    });
    const [expenses, setExpenses] = useState([]);
    const [parsedExpenses, setParsedExpenses] = useState([]);
    const [total, setTotal] = useState({
        amount: '',
        currency: ''
    });
    const [titleText, setTitleText] = useState('');
    const [filter, setFilter] = useState('');
    const titleRow = [{
        amount: t('offline_payments.amount'),
        bankReconsile: t('reports.bank_reconsile'),
        chequeNumber: t('reports.confirmation_id'),
        date: t('offline_payments.payment_date'),
        frequency: t('reports.frequency'),
        note: t('reports.note'),
        paid: t('offline_payments.amount'),
        paymentMethod: t('expenses.payment_method'),
        paymentTypeText: t('reports.payment_type'),
        vendor: t('expenses.name_of_supplier'),
        status: t('offline_payments.status')
    }];
    const [pdfContainerActive, setPdfContainerActive] = useState(false);
    const pdfContainerActivate = () => setPdfContainerActive(true);

    const expensesCallback = (item) => {
        setParsedExpenses(prev => prev.concat([item]));
    }

    const loadExpenses = async (requestedYear = year, requestedMonth = month) => {
        try {
            const { Total, Expenses } = await client.managers.expenses.getMonthlyExpenses(buildingID, requestedYear, requestedMonth);
            setTotal({
                amount: Total?.paid,
                currency: Total?.currency
            });
            Expenses.sort((a, b) => new Date(b.chargeDate) - new Date(a.chargeDate));
            setExpenses(Expenses);
        } catch (e) {
            return;
        }
    };

    useEffect(async () => {
        if (!client) return;

        dispatch(showLoader());

        if (!titleData.translationKey) dispatch(getBuildingTitle({buildingID}));

        const monthsTexts = getSelectMonthForDropdown(year, t);
        setMonthData({
            monthsTexts,
            selectedIndex: monthsTexts.findIndex(item => item.value === `${parseInt(month)}_${year}`)
        });

        await loadExpenses(year, month);

        dispatch(hideLoader());
    }, [client, year, month]);

    useEffect(() => {
        setTitleText(
            t('expenses.expense_monthly_report', {
                amount: StringUtils.numberWithCommas(total?.amount) || '-',
                currency: total?.currency}
            )
        )
    }, [total]);

    const renderExpenses = (expense, index, callback = () => {}) => {
        return (
            <ExpensesCard
                expense={expense}
                key={index}
                filter={filter}
                callback={callback}
                loadExpenses={loadExpenses}
                titleData={titleData}
                buildingID={buildingID}
            />
        )
    };

    const onMonthAndYearChanged = async monthYearData => {
        const parts = monthYearData.value.split('_');
        const newMonth = parts[0];
        const newYear = parts[1];
        const selectedIndex = monthData.monthsTexts.findIndex(option => option.value === `${parseInt(newMonth)}_${newYear}`)
        setMonthData({
            ...monthData,
            selectedIndex
        });
        const pathname = generatePath(path, { buildingID, month: newMonth, year: newYear });
        history.push({ pathname });
    };

    const renderPdfRows = (expense, i) => <ExpensesPdfRow expense={expense} key={i} />;

    return (
        <StyledMonthlyExpenses ltr={ltr}>
            <PageTitle text={t('reports.expenses_monthly_title')}>
                <PrintPdfDropdown 
                    classes='report-pdf-icons'
                    buildingID={buildingID}
                    eventLabel="apartmentCashflow"
                    titleString={titleText}
                    excelTitle={titleText}
                    reportName={t('reports.expenses_report_title')}
                    pdfContainer={pdfContainer.current}
                    rows={parsedExpenses}
                    titleRow={titleRow}
                    onClick={pdfContainerActivate}
                />
            </PageTitle>
            <StyledReport className='report' ltr={ltr}>
                <div className='report-wrap'>
                    <div className='report-top'>
                        <div className='report-top-start'>
                            <div className="search">
                                <input
                                    type="text"
                                    name="search"
                                    onChange={e => setFilter(e.target.value.toLowerCase())}
                                />
                                <img src={Search} alt={t('search.search')} />
                            </div>
                            <Select
                                className={'select'}
                                styles={newSelectStyles}
                                options={monthData.monthsTexts}
                                value={monthData.monthsTexts[monthData.selectedIndex]}
                                onChange={onMonthAndYearChanged}
                                isRtl={!ltr}
                            />
                        </div>
                    </div>
                </div>
            </StyledReport>

            {expenses?.length === 0 && <div className='no_records'>{t('expenses.bank_adjustments.no_records')}</div>}
            {expenses?.length > 0 && (
                <div className='expenses-wrapper'>
                    <div>
                        <div className='outside-headers'>
                            <div>{t('offline_payments.payment_date')}</div>
                            <div>{t('offline_payments.amount')}</div>
                            <div>{t('expenses.name_of_supplier')}</div>
                            <div>{t('reports.payment_type')}</div>
                            <div>{t('reports.frequency')}</div>
                            <div>{t('expenses.payment_method')}</div>
                            <div>{t('reports.confirmation_id')}</div>
                        </div>
                        <Scrollbar
                            style={{ width: '100%', height: 'calc(100vh - 400px)' }}
                            trackYProps={{
                                renderer: props => {
                                    const { elementRef, ...restProps } = props;
                                    return <div {...restProps} ref={elementRef} className="scroll-track" />;
                                }
                            }}
                            thumbYProps={{
                                renderer: props => {
                                    const { elementRef, ...restProps } = props;
                                    return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                                }
                            }}
                            rtl={false}
                            noScrollX
                        >
                            <div className='expenses-cards-wrapper'>
                                {expenses?.map((expense, i) => renderExpenses(expense, i, expensesCallback))}
                            </div>
                        </Scrollbar>
                    </div>
                </div>
            )}
            <div className='expenses-subtitle'>
                <span>{t('reports.expenses_monthly_subtitle')}:</span>
                <CurrencyValue currency={total.currency} notInCents={true}>{total.amount}</CurrencyValue>
            </div>

            <div id="section-to-print" ref={pdfContainer}>
            {pdfContainerActive && (
                <StyledPdfWrapper ltr={ltr}> 
                    <div className='pdf-title'>{`${t('reports.expenses_monthly_title')} ${month}, ${year}`}</div>

                    <div className='pdf-rows-wrapper'>
                        <div className='pdf-row'>
                            <div className='expenses pdf-highlited'>{t('offline_payments.payment_date')}</div>
                            <div className='expenses pdf-highlited'>{t('offline_payments.amount')}</div>
                            <div className='expenses pdf-highlited'>{t('expenses.name_of_supplier')}</div>
                            <div className='expenses pdf-highlited'>{t('reports.payment_type')}</div>
                            <div className='expenses pdf-highlited'>{t('reports.frequency')}</div>
                            <div className='expenses pdf-highlited'>{t('expenses.payment_method')}</div>
                            <div className='expenses pdf-highlited'>{t('reports.confirmation_id')}</div>
                        </div>
                    
                        {expenses?.map((expense, i) => renderPdfRows(expense, i))}

                        <div className='pdf-row'>
                            <div className='expenses pdf-highlited'>{t('reports.total')}</div>
                            <div className='expenses'>
                                <CurrencyValue currency={total.currency} notInCents={true}>{total.amount}</CurrencyValue>
                            </div>
                        </div>
                    </div>                   
                </StyledPdfWrapper>
            )}
            </div>
        </StyledMonthlyExpenses>
    );
};

export default MonthlyExpenses;
